import * as Sentry from "@sentry/react";
import { useT } from "@transifex/react";
import { CreationContext, type SilentErrorOptions } from "p6m-contexts";
import {
    AvailableUserOwnSubjectsInfo,
    BandCards,
    BandUnit,
    ExerciseCreationModel,
    GetBandInfoResponse,
    ReducedTestDetails,
    SelectedBookData,
    Settings,
    SidebarSelectedWordsList,
    StartPageRedirectionTarget,
    StudentTestContent,
    StudentTestContentWrapper,
    StudentTestSharingData,
    TeacherTestDetails,
    TestQuestionModel,
    UnitCard,
    UserOwnCard,
    UserSubject,
    UserUnit,
    UserUnitCards,
    VerbtrainingTensesResponseItem,
} from "p6m-p6u";
import React, { useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { invokeSurvicateEvent, SurvicateEvents } from "../services/survicate.service";
import { useHistory } from "react-router-dom";
import verbtrainingTenses from "../assets/verbtraining/verbtrainingTenses.json";
import { CreationModes } from "../constants/CreationFlowConstants";
import { VocabTrainingTitleTags } from "../enums/vocabTrainingTitleTags";
import { AddCardResults } from "../enums/cardSelection";
import { useLogger } from "../hooks/useLogger";
import { NetworkErrorCodes } from "../networking/errorCodes";
import { getCardsForUnit, getStudentTestById, getTeacherSharedTests, getUserTests } from "../networking/tests";
import { useGeneralContext } from "./AppGeneralContext";

import { ShareModes } from "../enums/shareFlow";
import {
    deleteRequest,
    getRequest,
    postRequest,
    processCardsForUnitResponse,
    putRequest,
    sortAndFilterUnitsForBandResponse,
} from "../helpers/networkHelper";
import { getStandardOrDefaultConfiguration } from "../helpers/PDFHelper";
import { buildSyncedStudentTestCopy, checkContentForErrors, removeAnswersFromTest } from "../helpers/ShareTest";
import {
    generateExerciseId,
    getExerciseKeyFromExerciseName,
    translateWordFromQuestionToModel,
    updateWordsInProgressDirection,
} from "../helpers/TestCreation";
import {
    composeBookName,
    ContentDetailsAndLoggingDataParams,
    getContentDetailsAndLoggingDataFromTest,
    reduceTestContent,
} from "../helpers/TestPreparationHelper";
import { resolveShareModeWithLegacy } from "../helpers/LegacyTest";
import { takeStoredBookDeepLinkParams } from "../helpers/DeepLinkParams";
import { reassignSharedTest } from "../helpers/SharedTestReassignment";
import { processUserUnitCardsResponse } from "../helpers/UserExercises";
import { toEpochMs, toEpochMsTimestamps } from "../utils/dateUtils";
import { addCardsToDrawerContent, buildSelectedWord } from "../helpers/VocabularySelection";
import {
    clearAnonymousTestHandoff,
    readAnonymousTestHandoff,
    type AnonymousTestHandoff,
} from "../helpers/AnonymousTestHandoff";

enum PXPErrorClasses {
    "Unauthorized" = "de.phase6.p6p.server.core.exception.UnauthorizedRequestException",
}

// Everything a copy must not inherit: the server-managed identity/timestamps, plus the source class and
// share flag. A copy that keeps `groupId` shows a visibility badge without ever going through the share
// flow, and one that keeps `shareMode` reads as shared with no student-test record behind it.
const _stripNonCopyableTestFields = (original: TeacherTestDetails) => {
    const { id, updatedDate, createdDate, groupId, shareMode, ...copyableContent } = original;
    return copyableContent;
};

/**
 * The persisted record owns the class assignment, absence included: a `creatingForSchoolClassId` left
 * over from an abandoned class flow must not attach its class to an unrelated test on save.
 */
const _persistedTestFields = (
    persistedTest: TeacherTestDetails
): Pick<ReducedTestDetails, "groupId" | "shareMode" | "createdDate"> => {
    const createdDate = toEpochMs(persistedTest.createdDate);

    return {
        groupId: persistedTest.groupId,
        ...(persistedTest.shareMode ? { shareMode: persistedTest.shareMode } : {}),
        ...(createdDate ? { createdDate } : {}),
    };
};

// `groupId` is sent as `null` (not omitted) so reassigning to "no class" reliably clears it on the BE.
const _buildTestCopyPayload = (
    original: TeacherTestDetails,
    newSchoolClassId: string | null,
    shareMode: ShareModes | null
) => ({
    ..._stripNonCopyableTestFields(original),
    groupId: newSchoolClassId,
    ...(shareMode !== null ? { shareMode } : {}),
});

// Builds a positive payload for re-sharing the copy. Source `sharedContent` is the prior
// `StudentTestContent`, which carries server-managed metadata (id/createdDate/resultsCount/...)
// that does not belong on a fresh `aea/student-tests/` POST.
// `groupId` must be the target class token — `sourceShared.groupId` still points at the old class,
// and without it the student-test loses its class linkage entirely (no classroom-login gate on the
// direct link, missing from the students' class dashboard).
const _buildSharedCopyPayload = (
    sourceShared: StudentTestContent,
    shareMode: ShareModes,
    newSchoolClassId: string | null
) => ({
    groupId: newSchoolClassId,
    content: sourceShared.content,
    name: sourceShared.name,
    teacherName: sourceShared.teacherName,
    bookInfo: sourceShared.bookInfo,
    ownSubjectInfo: sourceShared.ownSubjectInfo,
    language: sourceShared.language,
    articleId: sourceShared.articleId,
    settings: sourceShared.settings,
    showScores: sourceShared.showScores,
    visibilityDate: sourceShared.visibilityDate ?? null,
    dueDate: sourceShared.dueDate || 0,
    timeLimit: sourceShared.timeLimit ?? "",
    teacherComment: sourceShared.teacherComment ?? "",
    shareMode,
});

const TestCreationContext = React.createContext<CreationContext | undefined>(undefined);

// Serializes concurrent ADD saves: while one save is POSTing a brand-new test, any
// overlapping save reuses this promise's id (UPDATE) instead of POSTing a second, duplicate
// test. Reset when a fresh create/edit flow starts.
let addTestInFlightPromise: Promise<string> | null = null;
// Shared by overlapping callers (the initial fire-and-forget load and a click that arrives before it
// resolved), so the click never issues a second request.
let reloadUserSubjectsInFlight: Promise<Array<UserSubject> | null> | null = null;

type OwnSubjectsAction = "CONTINUE" | "POPUP" | "ERROR";

function useTestCreationContext() {
    const context = React.useContext(TestCreationContext);

    if (!context) {
        throw new Error("useTestCreationContext must be used inside the provider");
    }
    const {
        userId,
        setIsDataBeingLoaded,
        creatingMode,
        setCreatingMode,
        makePxpRequest,
        userInfo,
        creatingForSchoolClassId,
        hasTest,
        setHasTest,
    } = useGeneralContext();
    const [cookies] = useCookies(["GDPR", "JOSSO_SESSIONID", "TPU"]);
    const { logEvent } = useLogger();
    const history = useHistory();

    /** Prevents the anonymous-test reclaim from running twice (React 18 StrictMode double-mounts
     *  AssistantOverview, and the async reclaim only clears localStorage after its await — so both
     *  passes fire "Anonymous Test Reclaimed" before either cleans up). */
    const hasReclaimedAnonymousTest = useRef(false);

    const t = useT();

    const t_errorInsertingTest = t("There was an error while creating the new Test.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_errorUpdatingTest = t("There was an error while updating the new Test.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_errorSavingAnonymousTest = t("There was an error while saving your new Test.", {
        _tags: "CreateTest,CreateClass,AnonymousTest",
    });
    const t_errorWhileDeletingTest = t("There was an error while deleting the Test.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_errorWhileDuplicatingTest = t("There was an error while duplicating the Test.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_errorLoadingSubjectInfo = t("There was an error while loading subject information.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_errorWhileSharingTest = t("There was an error while sharing the Test.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_errorUpdatingStudentTest = t("There was an error while updating this test.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_errorUpdatingTestClass = t("There was an error while updating the class for this test.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_noTestFound = t("Couldn't find the test.", {
        _tags: "CreateTest,CreateClass",
    });
    const t_testHasNoContent = t("Test has no content", {
        _tags: "CreateTest,CreateClass",
    });

    const {
        setDataLoaded,
        availableVerbtrainingTenses,
        studentTestContentWrapper,
        studentTestContentWrapperRef,
        setStudentTestContentWrapper,
        setSelectedBookData,
        vocabularyDrawerContent,
        setVocabularyDrawerContent,
        setSelectedBandUnits,
        selectedBookData,
        availableCards,
        setAvailableCards,
        maxAmountOfCardsSelectable,
        setIsWarningModalVisible,
        setWarningModalContent,
        hasConfirmedLargeVocabTraining,
        setHasConfirmedLargeVocabTraining,
        pendingVocabCardsToAdd,
        setPendingVocabCardsToAdd,
        testContent,
        setTestContent,
        testName,
        currentTestId,
        setCurrentTestId,
        userTests,
        currentExerciseModel,
        setCurrentExerciseModel,
        settings,
        setSettings,
        showScores,
        setShowScores,
        setTestName,
        selectedTitleTag,
        setSelectedTitleTag,
        isTestBeingEdited,
        setIsTestBeingEdited,
        setPreviouslyUsedCardsIds,
        setPreviouslyUsedCardsClassName,
        setIsTestSharingInfoBeingLoaded,
        setUserTests,
        dataLoaded,
        setUserTestsLoadError,
        setUserOwnSubjects,
        setUserOwnSubjectUnits,
        setUserUnitCards,
        selectedUserSubject,
        setSelectedUserSubject,
        userOwnSubjects,
        haveOwnSubjectsLoaded,
        setHaveOwnSubjectsLoaded,
        setUserUsedOwnSubjectsInfo,
        userUsedOwnSubjectsInfo,
        setShouldSubjectSelectionBeDisabled,
        setUnitsToPreloadIds,
        setIsAnonymousTestClaimed,
    } = context;

    async function deleteTest(testId: string) {
        try {
            await deleteRequest(`aea/tests/?testId=${testId}`);
        } catch (e) {
            console.log(e);
            openWarningModal(t_errorWhileDeletingTest);
            return;
        }

        setDataLoaded(false);
        let remainingCount: number | undefined;
        try {
            remainingCount = await loadUserTests(true);
        } catch {
            // Leave hasTest unchanged so we don't flip to false on a transient error.
        }

        if (typeof remainingCount === "number") {
            setHasTest(remainingCount > 0);
            logEvent("Vokabeltest v2 Deleted", {
                testId,
                onboarding_test_created: remainingCount > 0,
            });
        } else {
            logEvent("Vokabeltest v2 Deleted", { testId });
        }
    }

    async function duplicateTest(testId: string, testName: string, schoolClassId?: string) {
        try {
            // Step 1: Get the original test data.
            const baseTestDetails: TeacherTestDetails = userTests.find((t) => t.id === testId);

            if (baseTestDetails) {
                // Duplicates start unassigned and unshared; a class is only attached when an explicit
                // schoolClassId is provided (Step 3).
                const testContent = _stripNonCopyableTestFields(baseTestDetails);

                // Step 2: Create a copy of that data, and get the new id.
                const copyTestResult = await postRequest("aea/tests/", {
                    ...testContent,
                    name: testName,
                });

                const newTestId = copyTestResult.data.id;

                if (schoolClassId) {
                    await putRequest(`aea/tests/?testId=${newTestId}`, {
                        ...testContent,
                        name: testName,
                        id: newTestId,
                        groupId: schoolClassId,
                    });
                }

                logEvent("Vokabeltest v2 Duplicated", {
                    newTestId,
                    duplicatedTestId: testId,
                });

                setDataLoaded(false);
                await loadUserTests(true);
            } else {
                throw new Error("Test not found");
            }
        } catch (e) {
            openWarningModal(t_errorWhileDuplicatingTest);
        }
    }

    /**
     * Creates the test data for the preview based on the main context's data
     * as the preview uses a special format of test
     * @returns a StudentTestContent, a teacher test with the answers removed
     */
    function createPreviewData() {
        const testToUse = [...testContent];

        const answerFreeTestContent = removeAnswersFromTest(testToUse, availableVerbtrainingTenses);
        if (answerFreeTestContent.length === 0) {
            // nothing to preview
            return {};
        }

        const bookName = `${selectedBookData.publisherBook?.BookName} (${selectedBookData.publisherBook?.PublisherName}) (${selectedBookData.band?.ShortName})`;

        // shareMode is persisted on the share record, not on testContent. Without surfacing it
        // here, the preview can't tell TEST from PRACTICE and the overview defaults to the TEST
        // "Submit answers" / preview-popup combo even for practices.
        const currentTestDetails = currentTestId ? userTests.find((t) => t.id === currentTestId) : undefined;

        const testToStore: StudentTestContent = {
            content: answerFreeTestContent,
            dueDate: 0,
            teacherName: userInfo.userName,
            teacherComment: "",
            timeLimit: "",
            showScores,
            settings,
            name: testName,
            updatedDate: new Date().getTime(),
            createdDate: new Date().getTime(),
            bookInfo: {
                name: bookName,
                imagePath: selectedBookData.band?.Image ?? "",
            },
            ownSubjectInfo: {
                name: selectedUserSubject?.name,
                primaryLang: selectedUserSubject?.primaryLang,
                secondaryLang: selectedUserSubject?.secondaryLang || "",
                id: selectedUserSubject?.id,
                ownerId: selectedUserSubject?.ownerId,
            },
            language: selectedBookData.band?.Language,
            articleId: selectedBookData.band?.ID,
            id: currentTestId || `preview${new Date().getTime()}`,
            shareMode: currentTestDetails?.shareMode,
        };

        return testToStore;
    }

    async function updateSharedTestProperties(testId: string, sharingData: StudentTestSharingData): Promise<boolean> {
        const testToShare = studentTestContentWrapper[testId];

        if (!testToShare) {
            openWarningModal(t_noTestFound);
            return false;
        }
        if (!testToShare.content) {
            openWarningModal(t_testHasNoContent);
            return false;
        }

        try {
            const updatedTest = {
                ...testToShare,
                ...toEpochMsTimestamps(testToShare),
                visibilityDate: sharingData.visibilityDate ?? null,
                // `|| 0` not `?? 0`: legacy records read back from the BE can still carry "", which is
                // the other "no deadline" spelling this normalisation exists to retire.
                dueDate: sharingData.dueDate || 0,
                // Normalised alongside so the PUT is a consistent full overwrite. Left raw, an omitted
                // field would be dropped by JSON.stringify and preserved on the BE while the local
                // record loses it — the FE/BE divergence this ticket is closing.
                timeLimit: sharingData.timeLimit ?? "",
                teacherComment: sharingData.teacherComment ?? "",
            };

            await putRequest(`aea/student-tests/?testId=${testId}`, {
                ...updatedTest,
            });

            const allTests = { ...studentTestContentWrapper };
            allTests[testId] = {
                ...updatedTest,
                id: testId,
            };
            setStudentTestContentWrapper(allTests);

            logEvent("VTG - Assignment Properties Updated", {
                testId: testId,
                assignmentDeadline: updatedTest.dueDate,
                assignmentTime: updatedTest.timeLimit,
                assignmentVisibilityDate: updatedTest.visibilityDate,
            });
            return true;
        } catch (e) {
            console.log(e);
            openWarningModal(t_errorUpdatingStudentTest);
            return false;
        }
    }

    /** Call after the teacher test is persisted: a failed sync only warns, the teacher save is never rolled back. */
    async function syncSharedTestCopy(testId: string, teacherTest: TeacherTestDetails): Promise<void> {
        const storedCopy = await resolveStoredStudentCopy(testId);
        if (!storedCopy) return;

        const contentErrors = checkContentForErrors(teacherTest.content ?? []);
        if (contentErrors.length > 0) {
            openWarningModal(contentErrors.join("\n"));
            return;
        }

        const normalizedCopy = { ...storedCopy, ...toEpochMsTimestamps(storedCopy) };
        const syncedCopy = buildSyncedStudentTestCopy(normalizedCopy, teacherTest, availableVerbtrainingTenses);

        try {
            await putRequest(`aea/student-tests/?testId=${testId}`, syncedCopy);
            setStudentTestContentWrapper((prev) => ({ ...prev, [testId]: { ...syncedCopy, id: testId } }));
        } catch (e) {
            console.error("Failed to sync the student copy of a shared test", e);
            openWarningModal(t_errorUpdatingStudentTest);
        }
    }

    /**
     * Read through the ref: PDFConfiguration calls the sync from a memoised debounce whose closure never
     * re-renders. A missing entry is no proof the test is unshared, since the sharing data loads un-awaited
     * and may have failed, so the server is asked; only its 404 means "not shared".
     */
    async function resolveStoredStudentCopy(testId: string): Promise<StudentTestContent | undefined> {
        const cachedCopy = studentTestContentWrapperRef.current[testId];
        if (cachedCopy) return cachedCopy;

        try {
            return await getStudentTestById(testId);
        } catch (e) {
            console.error("Failed to look up the student copy of a shared test", e);
            openWarningModal(t_errorUpdatingStudentTest);
            return undefined;
        }
    }

    async function shareTest(params: {
        testId: string;
        testInfo: StudentTestSharingData;
        shareMode: ShareModes;
        effectiveSchoolClassId?: string | null;
        options?: SilentErrorOptions;
    }) {
        const { testId, testInfo, shareMode, effectiveSchoolClassId, options } = params;
        const _reportOrThrow = (errorCode: string, modalMessage: string): void => {
            if (options?.silentOnError) throw new Error(errorCode);
            openWarningModal(modalMessage);
        };

        const testToShare = userTests.find((t) => t.id === testId);

        if (!testToShare) {
            _reportOrThrow("test-not-found", t_noTestFound);
            return;
        }
        if (!testToShare.content) {
            _reportOrThrow("test-has-no-content", t_testHasNoContent);
            return;
        }

        try {
            const resolvedGroupId =
                effectiveSchoolClassId === undefined ? testToShare.groupId : effectiveSchoolClassId ?? undefined;
            const testToShareCopy = { ...testToShare, groupId: resolvedGroupId };
            const contentErrors = checkContentForErrors(testToShareCopy.content);
            if (contentErrors.length > 0) {
                _reportOrThrow("content-errors", contentErrors.join("\n"));
                return;
            }

            const answerFreeTestContent = removeAnswersFromTest(testToShareCopy.content, availableVerbtrainingTenses);
            testToShareCopy.content = answerFreeTestContent;

            const { visibilityDate, dueDate, teacherComment, timeLimit } = testInfo;
            const testConditions = {
                visibilityDate: visibilityDate ?? null,
                // 0 is the canonical "no deadline" sentinel (see StudentTestSharingData) — never "".
                dueDate: dueDate || 0,
                teacherName: userInfo.userName,
                teacherComment: teacherComment || "",
                timeLimit: timeLimit || "",
            };

            const testToSave = {
                ...testToShareCopy,
                ...testConditions,
                ...toEpochMsTimestamps(testToShareCopy),
                shareMode,
            };

            if (testToShare.shareMode !== shareMode) {
                await putRequest(`aea/tests/?testId=${testId}`, {
                    ...testToShare,
                    ...toEpochMsTimestamps(testToShare),
                    shareMode,
                    groupId: resolvedGroupId ?? null,
                });
                setUserTests((prev) =>
                    prev.map((t) =>
                        t.id === testId ? { ...t, ...toEpochMsTimestamps(t), shareMode, groupId: resolvedGroupId } : t
                    )
                );
            }

            await postRequest(`aea/student-tests/?testId=${testId}`, { ...testToSave });
            const updatedTestContentWrapper = { ...studentTestContentWrapper };
            updatedTestContentWrapper[testId] = { ...testToSave, id: testId };
            setStudentTestContentWrapper(updatedTestContentWrapper);
        } catch (e) {
            if (!options?.silentOnError) {
                openWarningModal(t_errorWhileSharingTest);
            }
            throw e;
        }
    }

    const createBasicTestData = (
        currentDate: Date,
        bookCardsCount: number,
        bookName: string,
        ownCardsCount: number
    ): TeacherTestDetails => {
        let basicTestData: any = {
            name: testName,
            updatedDate: currentDate.getTime(),
            createdDate: currentDate.getTime(),
            showScores,
            settings,
            ...(creatingForSchoolClassId ? { groupId: creatingForSchoolClassId } : {}),
        };

        if (bookCardsCount) {
            basicTestData = {
                articleId: selectedBookData.band?.ID,
                language: selectedBookData.band?.Language,
                bookInfo: {
                    name: bookName,
                    imagePath: selectedBookData.band?.Image,
                },
                ...basicTestData,
            };
        }

        if (ownCardsCount) {
            basicTestData = {
                ownSubjectInfo: {
                    name: selectedUserSubject.name,
                    primaryLang: selectedUserSubject.primaryLang,
                    secondaryLang: selectedUserSubject?.secondaryLang || "",
                    id: selectedUserSubject.id,
                    ownerId: selectedUserSubject.ownerId,
                },
                ...basicTestData,
            };
        }

        return basicTestData;
    };

    /**
     * @param testId the record the details are built for; saveTest passes the id it resolved from an
     * in-flight ADD, which `currentTestId` does not carry yet
     */
    const prepareReducedTestDetails = (
        newTestContent: TestQuestionModel[],
        testId = currentTestId.trim()
    ): ContentDetailsAndLoggingDataParams & { reducedTestDetails: ReducedTestDetails } => {
        const currentDate = new Date();
        const reducedTestContent = reduceTestContent(newTestContent);
        const bookName = composeBookName(selectedBookData);

        const { ownCardsCount, bookCardsCount, loggingContent, loggingContentExtendedBookInformation, amplitudeData } =
            getContentDetailsAndLoggingDataFromTest(testId, reducedTestContent, selectedBookData);

        let basicTestData = createBasicTestData(currentDate, bookCardsCount, bookName, ownCardsCount);

        // The BE PUT replaces the whole record: without the persisted class, share mode and creation date
        // the save would silently drop the class assignment and un-share the test.
        const persistedTest = userTests.find((t) => t.id === testId);

        const reducedTestDetails: ReducedTestDetails = {
            ...basicTestData,
            ...(persistedTest ? _persistedTestFields(persistedTest) : {}),
            content: reducedTestContent,
        };

        return {
            reducedTestDetails,
            ownCardsCount,
            bookCardsCount,
            loggingContent,
            loggingContentExtendedBookInformation,
            amplitudeData,
        };
    };

    /**
     * @param newTestContent contains the exercises
     * @returns bundled test details from the context, INCLUDING the id if present
     */
    const prepareFullTestDetails = (newTestContent: TestQuestionModel[]) => {
        const { reducedTestDetails } = prepareReducedTestDetails(newTestContent);
        const fullTestDetails = { ...reducedTestDetails, id: currentTestId };

        return fullTestDetails;
    };

    /**
     * @param authenticatedUserId set when the caller has just authenticated the teacher: the JOSSO cookie is
     * already live, but `userInfo`/`userId` are React state this closure cannot see yet (see SummaryModals'
     * register flow). It marks the save as authenticated instead of anonymous; ownership comes from the cookie.
     */
    async function saveTest(
        newTestContent: TestQuestionModel[],
        sendEvent = false,
        authenticatedUserId = ""
    ): Promise<AnonymousTestHandoff | undefined> {
        const isExistingUser = !!(userId || authenticatedUserId);
        const isAnonymous = userInfo.isAnonymousUser && !authenticatedUserId;
        const shouldEmitEvent = !isAnonymous && sendEvent;

        if (!isExistingUser && !isAnonymous) {
            return Promise.reject("isAnonymous user or not userId set");
        }

        let targetTestId = currentTestId.trim();

        // If an ADD is already creating this test, wait for it and reuse its id so
        // this save UPDATEs instead of POSTing a second (duplicate) test. Closes the read-modify-write
        // race on currentTestId when two saves overlap (e.g. the autosave debounce and the title/finish save).
        if (!targetTestId && addTestInFlightPromise) {
            try {
                targetTestId = (await addTestInFlightPromise).trim();
            } catch (e) {
                // If the in-flight ADD rejected after its POST already committed server-side, the
                // fall-through ADD below can still create a duplicate. Log so that case is diagnosable.
                console.warn("in-flight ADD failed while waiting for its id", e);
                targetTestId = "";
            }
        }

        // Anonymous sessions may only create tests (a PUT on an existing one is rejected with 403), so every
        // anonymous save POSTs a new test; the localStorage handoff below then points at the latest one.
        const savingMode = targetTestId.length > 0 && !isAnonymous ? "UPDATE" : "ADD";

        // prepare test data first - - - - - - - - - - - - - - - - - - - - - - - - -

        const { reducedTestDetails, loggingContent, loggingContentExtendedBookInformation, amplitudeData } =
            prepareReducedTestDetails(newTestContent, targetTestId);

        const testRequestContent = reducedTestDetails;

        if (isAnonymous && cookies.TPU) {
            testRequestContent.TPU = cookies.TPU;
        }

        const possibleWarningText = isAnonymous
            ? t_errorSavingAnonymousTest
            : savingMode === "ADD"
            ? t_errorInsertingTest
            : t_errorUpdatingTest;
        const possiblePromiseRejectText = isAnonymous
            ? "Error while saving anonymous test. "
            : `Error when ${savingMode === "ADD" ? "saving" : "updating"} test. `;

        if (isAnonymous) {
            setIsDataBeingLoaded(true); // loading screen while saving
        }

        // save - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        if (savingMode === "ADD") {
            // Register the in-flight ADD so overlapping saves reuse this id instead of creating a duplicate.
            const addPromise = postRequest("aea/tests/", { ...testRequestContent }).then((addedTestResponse) => {
                const newId: string = addedTestResponse.data.id;
                setCurrentTestId(newId);
                return newId;
            });
            addTestInFlightPromise = addPromise;
            try {
                targetTestId = await addPromise;
                // Intentionally NOT nulled on success: it lingers as a resolved-promise fallback so an
                // overlapping save whose currentTestId closure is still "" can reuse this id (UPDATE) before
                // setCurrentTestId propagates. Every new-test/reset path MUST null it (see loadTestForEdition
                // and the create-reset) or a later flow would UPDATE this test instead of creating a new one.
            } catch (e) {
                addTestInFlightPromise = null;
                if (isAnonymous) setIsDataBeingLoaded(false);
                openWarningModal(possibleWarningText);
                return Promise.reject(possiblePromiseRejectText + e);
            }
        } else {
            try {
                await putRequest(`aea/tests/?testId=${targetTestId}`, { ...testRequestContent });
            } catch (e) {
                if (isAnonymous) setIsDataBeingLoaded(false);
                openWarningModal(possibleWarningText);
                return Promise.reject(possiblePromiseRejectText + e);
            }
            await syncSharedTestCopy(targetTestId, testRequestContent);
        }

        if (shouldEmitEvent) {
            if (isTestBeingEdited) {
                logEvent("Vokabeltest v2 Edited", { ...loggingContent, points_enabled: showScores });
            } else {
                logEvent("Vokabeltest v2 Saved", {
                    ...loggingContentExtendedBookInformation,
                    testId: targetTestId,
                    points_enabled: showScores,
                    onboarding_test_created: true,
                });
                invokeSurvicateEvent(SurvicateEvents.VOKABELTEST_SAVED);
            }
        }

        if (shouldEmitEvent && !hasTest) {
            logEvent("First Test Created", { onboarding_test_created: true });
            setHasTest(true);
        }

        if (isAnonymous) {
            setIsDataBeingLoaded(false);
            // The caller persists this only right before the login redirect, so an abandoned popup
            // leaves nothing for AnonymousTestClaimer to pick up.
            return { testId: targetTestId, amplitudeData };
        }
    }

    async function checkForAnonymousTests() {
        const { testId: anonymousTestId, amplitudeData } = readAnonymousTestHandoff();

        if (anonymousTestId !== "" && userId && !hasReclaimedAnonymousTest.current) {
            hasReclaimedAnonymousTest.current = true;
            setIsAnonymousTestClaimed(false);
            // setting this to avoid triggering the redirect to the select content screen.
            setDataLoaded(true);
            setIsDataBeingLoaded(true);

            try {
                const testContentResponse = await getRequest(`aea/tests/?testId=${anonymousTestId}`);
                const anonymousTestContent = testContentResponse.data;
                if (!anonymousTestContent.content) {
                    openWarningModal(t_testHasNoContent);
                    clearAnonymousTestHandoff();
                    setIsDataBeingLoaded(false);
                    return;
                }

                // Updating test with new user id.
                await putRequest(`aea/tests/?testId=${anonymousTestId}`, { ...anonymousTestContent });

                logEvent("Anonymous Test Reclaimed", { test_id: anonymousTestId });

                clearAnonymousTestHandoff();

                // setHasTest triggers a re-render; logEvent's closure still sees the old value — onboarding_test_created: true overrides it explicitly.
                setHasTest(true);
                if (amplitudeData.hasOwnProperty("testId")) {
                    logEvent("Vokabeltest v2 Saved", { ...amplitudeData, onboarding_test_created: true });
                    invokeSurvicateEvent(SurvicateEvents.VOKABELTEST_SAVED);
                }

                setDataLoaded(false);
                setIsAnonymousTestClaimed(true);
            } catch (e: any) {
                clearAnonymousTestHandoff();
                // A 404 means the test is gone: nothing to save, so drop the stale id instead of
                // alarming the teacher about a save they never asked for.
                if (e?.response?.status !== NetworkErrorCodes.NOT_FOUND) {
                    Sentry.captureException(e, { extra: { stage: "anonymous test reclaim", anonymousTestId } });
                    openWarningModal(t_errorSavingAnonymousTest);
                }
            }

            const userTestCount = await loadUserTests(true);
            setIsDataBeingLoaded(false);

            // First test for anon→login users lands here: saveTest skips its event path for
            // anonymous saves, so this reclaim is where their first test gets counted. === 1 means
            // the just-reclaimed test is now their only one (server truth, so returning teachers
            // with count > 1 don't fire it, and a failed reclaim leaves count 0).
            if (userTestCount === 1) {
                logEvent("First Test Created", { onboarding_test_created: true });
            }
        }
    }

    async function loadVocabularyForBand(bandId: string) {
        getRequest("p6u/get-units-for-band/", { bandId }).then((res) => {
            if (res.data && res.data.length > 0) {
                //Hide hidden and sort units by order_id
                setSelectedBandUnits(sortAndFilterUnitsForBandResponse(res.data));
            } else {
                alert("error getting units");
            }
        });
    }

    async function loadCardsForUnits(unitIds: string[]) {
        const updatedCards: BandCards = {};

        for (let unitId of unitIds) {
            try {
                const unitContentResponse = await getCardsForUnit(unitId);
                if (unitContentResponse) {
                    const newCardsList: Array<UnitCard> = processCardsForUnitResponse(unitId, unitContentResponse);
                    updatedCards[unitId] = {
                        count: unitContentResponse.count,
                        items: newCardsList,
                    };
                }
            } catch (e) {
                console.log("error loading unit " + unitId);
            }
        }

        setAvailableCards(updatedCards);
        return Promise.resolve();
    }

    function loadCardsForUnit(unitId: string) {
        const requestParams: any = { unitId: unitId };

        if (userId) {
            requestParams["userId"] = userId;
        }

        getCardsForUnit(unitId).then(
            (res) => {
                const currentCards = { ...availableCards };
                if (res) {
                    const newCardsList: Array<UnitCard> = processCardsForUnitResponse(unitId, res);
                    currentCards[unitId] = {
                        count: res.count,
                        items: newCardsList,
                    };
                }
                setAvailableCards(currentCards);
                setIsDataBeingLoaded(false);
            },
            () => {
                setIsDataBeingLoaded(false);
            }
        );
    }

    function removeCardFromSelectedWords(wordId: string) {
        const words = { ...vocabularyDrawerContent };
        const wordToDelete = words[wordId];

        //Remove card from exercises.
        if (wordToDelete.exerciseIds) {
            const testContentCopy = [...testContent];
            for (let i = 0; i < testContentCopy.length; i++) {
                const testItem = { ...testContentCopy[i] };
                if (
                    testItem.exerciseId &&
                    wordToDelete.exerciseIds.includes(testItem.exerciseId) &&
                    testItem.selectedWords
                ) {
                    testItem.selectedWords = testItem.selectedWords.filter((w) => {
                        if ("id" in w) {
                            return String(w.id) !== String(wordId);
                        } else if ("ID" in w) {
                            return String(w.ID) !== String(wordId);
                        }
                        return false;
                    });
                    testContentCopy[i] = testItem;
                }
            }
            setTestContent(testContentCopy);
        }
        delete words[wordId];
        setVocabularyDrawerContent(words);
    }

    function removeCardsFromSelectedWords(wordIds: Array<string>) {
        const words = { ...vocabularyDrawerContent };
        let exercisesCopy = [...testContent];

        const exercisesWithWordsToDelete: { [id: string]: Array<string> } = {};

        // Deleting words from the drawer, adding the exercises to a list of exercises and words
        // that needs to be removed.
        wordIds.forEach((w) => {
            const wordContent = words[w];
            if (wordContent) {
                if (wordContent.exerciseIds && wordContent.exerciseIds.length > 0) {
                    wordContent.exerciseIds.forEach((eId) => {
                        if (exercisesWithWordsToDelete.hasOwnProperty(eId)) {
                            if (!exercisesWithWordsToDelete[eId].includes(w)) {
                                exercisesWithWordsToDelete[eId].push(w);
                            }
                        } else {
                            exercisesWithWordsToDelete[eId] = [w];
                        }
                    });
                }
                delete words[w];
            }
        });

        const exercisesToDelete: Array<string> = [];

        for (let i = 0; i < exercisesCopy.length; i++) {
            const exercise = { ...exercisesCopy[i] };
            if (!exercise || !exercise.exerciseId) {
                return;
            }

            // If the exercise has pending words to delete, delete them, amd if its empty, put it on list to delete.
            if (Object.keys(exercisesWithWordsToDelete).includes(exercise.exerciseId) && exercise.selectedWords) {
                exercise.selectedWords =
                    exercise.selectedWords.filter((w) => {
                        if ("id" in w && w.id) {
                            return !exercisesWithWordsToDelete[exercise.exerciseId!].includes(w.id);
                        } else if ("ID" in w && w.ID) {
                            return !exercisesWithWordsToDelete[exercise.exerciseId!].includes(w.ID);
                        }
                        return false;
                    }) || [];

                if (exercise.selectedWords.length === 0) {
                    exercisesToDelete.push(exercise.exerciseId);
                }
            }
            exercisesCopy[i] = exercise;
        }

        if (exercisesToDelete.length > 0) {
            exercisesCopy = exercisesCopy.filter((e) => {
                return e.exerciseId && !exercisesToDelete.includes(e.exerciseId);
            });
        }

        setVocabularyDrawerContent(words);
        setTestContent(exercisesCopy);
    }

    function addCardToSelectedWords(card: UnitCard): AddCardResults {
        const words = { ...vocabularyDrawerContent };
        const isAlreadySelected = !!words[card.id!];

        if (creatingMode === CreationModes.RECURRING_TASK) {
            // Vocab trainings have no hard cap; confirm once when crossing the threshold.
            if (isAlreadySelected) {
                return AddCardResults.ADDED;
            }
            if (Object.keys(words).length + 1 > maxAmountOfCardsSelectable && !hasConfirmedLargeVocabTraining) {
                setPendingVocabCardsToAdd([card]);
                return AddCardResults.PENDING_CONFIRMATION;
            }
            setVocabularyDrawerContent(addCardsToDrawerContent(words, [card]));
            return AddCardResults.ADDED;
        }

        // Tests keep the hard cap.
        if (Object.keys(words).length >= maxAmountOfCardsSelectable) {
            return AddCardResults.CAP_REACHED;
        }
        if (!isAlreadySelected) {
            setVocabularyDrawerContent(addCardsToDrawerContent(words, [card]));
        }
        return AddCardResults.ADDED;
    }

    function addCardsToSelectedWords(cards: Array<UnitCard>): AddCardResults {
        const words = { ...vocabularyDrawerContent };

        if (creatingMode === CreationModes.RECURRING_TASK) {
            // Vocab trainings have no hard cap; confirm once when crossing the threshold.
            const newCards = cards.filter((card) => !words[card.id!]?.wordContent?.id);
            if (
                Object.keys(words).length + newCards.length > maxAmountOfCardsSelectable &&
                !hasConfirmedLargeVocabTraining
            ) {
                setPendingVocabCardsToAdd(cards);
                return AddCardResults.PENDING_CONFIRMATION;
            }
            setVocabularyDrawerContent(addCardsToDrawerContent(words, newCards));
            return AddCardResults.ADDED;
        }

        // Tests keep the hard cap.
        if (Object.keys(words).length >= maxAmountOfCardsSelectable) {
            return AddCardResults.CAP_REACHED;
        }

        let response = AddCardResults.ADDED;

        for (let i = 0; i < cards.length; i++) {
            const card = cards[i];
            if (words[card.id!]?.wordContent?.id) {
                continue;
            }
            words[card.id!] = buildSelectedWord(card);
            if (Object.keys(words).length >= maxAmountOfCardsSelectable) {
                response = i === cards.length - 1 ? AddCardResults.ADDED : AddCardResults.INCOMPLETE_ADD;
                break;
            }
        }

        setVocabularyDrawerContent(words);
        return response;
    }

    function confirmLargeVocabTraining() {
        setHasConfirmedLargeVocabTraining(true);
        setVocabularyDrawerContent(addCardsToDrawerContent(vocabularyDrawerContent, pendingVocabCardsToAdd));
        setPendingVocabCardsToAdd([]);
    }

    function cancelLargeVocabTraining() {
        setPendingVocabCardsToAdd([]);
    }

    function _replaceDrawerContent(cards: Array<UnitCard>) {
        // Goes through the regular removal so exercises referencing the old words are pruned too.
        removeCardsFromSelectedWords(Object.keys(vocabularyDrawerContent));
        setVocabularyDrawerContent(addCardsToDrawerContent({}, cards));
    }

    function setDrawerContentFromCards(cards: Array<UnitCard | UserOwnCard>): AddCardResults {
        const unitCards = cards as Array<UnitCard>;

        // Vocab trainings have no hard cap; the caller confirms large selections before seeding.
        if (creatingMode === CreationModes.RECURRING_TASK) {
            _replaceDrawerContent(unitCards);
            return AddCardResults.ADDED;
        }

        const cardsWithinCap = unitCards.slice(0, maxAmountOfCardsSelectable);
        _replaceDrawerContent(cardsWithinCap);
        return unitCards.length > cardsWithinCap.length ? AddCardResults.INCOMPLETE_ADD : AddCardResults.ADDED;
    }

    /** `silent` skips the loading state and warning modal, so best-effort metadata writes can't disrupt a share. */
    async function loadUserOwnSubjectUUIDsInfoIntoState(subjectUUIDS: Array<string>, options?: { silent?: boolean }) {
        const { silent } = options ?? {};
        if (!silent) setIsDataBeingLoaded(true);
        const subjectsInfo: AvailableUserOwnSubjectsInfo = { ...userUsedOwnSubjectsInfo };

        for (let i = 0; i < subjectUUIDS.length; i++) {
            const subjectUuid = subjectUUIDS[i];
            if (subjectsInfo.hasOwnProperty(subjectUuid) && subjectsInfo[subjectUuid].id) {
                continue;
            }
            try {
                const userSubjectsResponse = await makePxpRequest("GET", `/${userId}/subjects/${subjectUuid}`);
                if (userSubjectsResponse.data.replyContent) {
                    subjectsInfo[subjectUuid] = {
                        ...userSubjectsResponse.data.replyContent,
                        id: subjectUuid,
                    };
                } else {
                    throw new Error("No data was loaded");
                }
            } catch (e) {
                console.error("Failed to load own subject info", e);
                Sentry.captureException(e, { extra: { subjectUuid } });
                if (!silent) {
                    openWarningModal(t_errorLoadingSubjectInfo);
                    setIsDataBeingLoaded(false);
                }
                return;
            }
        }
        if (!silent) setIsDataBeingLoaded(false);
        setUserUsedOwnSubjectsInfo({ ...subjectsInfo });
        return subjectsInfo;
    }

    function openWarningModal(content: React.ReactNode) {
        setWarningModalContent(content);
        setIsWarningModalVisible(true);
    }

    function updateCurrentExerciseProperties(newProperties: ExerciseCreationModel) {
        const updatedExercise = { ...currentExerciseModel, ...newProperties };

        if (
            typeof newProperties.direction !== "undefined" &&
            currentExerciseModel.direction !== newProperties.direction &&
            updatedExercise.wordsInProgress
        ) {
            updatedExercise.wordsInProgress = updateWordsInProgressDirection(
                updatedExercise.wordsInProgress,
                newProperties.direction
            );
        }

        setCurrentExerciseModel(updatedExercise);
    }

    /**
     * Loads the test data into the main context (TextCreationContext)
     * This is used for editing tests, the preview and configuration
     * @param testId the id of the test to load
     * @param checkContent checks the content for errors
     * -> prevents opening previews / configurations with broken content
     */
    async function loadTestForEdition(testId: string, checkContent?: boolean) {
        setIsDataBeingLoaded(true);
        setCurrentTestId(testId);
        addTestInFlightPromise = null; // editing an existing test starts a fresh flow

        const localTestData: TeacherTestDetails = userTests.find((t: TeacherTestDetails) => t.id === testId);

        if (!localTestData) {
            openWarningModal(t_noTestFound);
            return;
        }

        if (!localTestData.content) {
            openWarningModal(t_testHasNoContent);
            return;
        }

        const newTestContent: Array<TestQuestionModel> = [];
        const newDrawerContent: SidebarSelectedWordsList = {};

        localTestData.content?.forEach((exercise) => {
            const newExerciseData: TestQuestionModel = { ...exercise };

            // If the exercise type is unknown, then skip the exercise, otherwise generate an id.
            if (!newExerciseData.exerciseId) {
                const exerciseKey = getExerciseKeyFromExerciseName(exercise.questionMode || "");
                if (exerciseKey) {
                    newExerciseData.exerciseId = generateExerciseId(exerciseKey, testContent);
                } else {
                    return;
                }
            }

            if (!newExerciseData.hasOwnProperty("automaticallyCreated")) {
                newExerciseData.automaticallyCreated = false;
            }

            newTestContent.push(newExerciseData);

            newExerciseData.selectedWords?.forEach((w) => {
                if ("id" in w && w.id) {
                    if (newDrawerContent.hasOwnProperty(w.id)) {
                        newDrawerContent[w.id].exerciseIds!.push(newExerciseData.exerciseId!);
                        newDrawerContent[w.id].isInMoreThanOneExercise = true;
                    } else {
                        newDrawerContent[w.id] = {
                            wordContent: translateWordFromQuestionToModel(w),
                            isInMoreThanOneExercise: false,
                            exerciseIds: [newExerciseData.exerciseId!],
                        };
                    }
                }
            });
        });

        const standardOrDefaultSettings = getStandardOrDefaultConfiguration().settings;

        if (checkContent) {
            const contentErrors = checkContentForErrors(newTestContent);
            if (contentErrors.length > 0) {
                setCurrentTestId("");
                setTestContent([]);
                setTestName("");
                setShowScores(false);
                setSettings(standardOrDefaultSettings);
                setIsDataBeingLoaded(false);
                return Promise.reject({ contentErrors });
            }
        }

        setTestContent(newTestContent);
        setTestName(localTestData.name || "");
        setSelectedTitleTag(null);
        setShowScores(localTestData.showScores ?? false);
        setSettings(localTestData.settings ?? standardOrDefaultSettings);

        try {
            if (localTestData.articleId) {
                await loadInitArticleIdData(localTestData.articleId);
            }
            if (localTestData.ownSubjectInfo && localTestData.ownSubjectInfo.id) {
                const ownSubjectsResponse = await loadUserOwnSubjectUUIDsInfoIntoState([
                    localTestData.ownSubjectInfo.id,
                ]);
                if (ownSubjectsResponse) {
                    const userSubject = ownSubjectsResponse[localTestData.ownSubjectInfo.id];
                    setSelectedUserSubject(userSubject);
                    setShouldSubjectSelectionBeDisabled(true);
                }
            }
        } catch (e) {
            setCurrentTestId("");
            setTestContent([]);
            setTestName("");
            setShowScores(false);
            setSettings(standardOrDefaultSettings);
            setIsDataBeingLoaded(false);
            return Promise.reject(e);
        }

        setIsTestBeingEdited(true);
        setCreatingMode(CreationModes.TEST);

        setVocabularyDrawerContent(newDrawerContent);
        setIsDataBeingLoaded(false);
    }

    async function loadInitArticleIdData(
        articleId: string,
        isUuid: boolean = false,
        shouldSetSelectedBookData: boolean = true
    ) {
        const requestParameters: { bandId: string; isUuid?: boolean } = {
            bandId: articleId,
        };
        if (isUuid) {
            requestParameters.isUuid = true;
        }
        const bandDataResponse = await getRequest("p6u/get-band-info/", requestParameters);
        if (!bandDataResponse || !bandDataResponse.data) {
            return Promise.reject(new Error("No data was loaded"));
        }
        const bandData: GetBandInfoResponse = bandDataResponse.data;
        if (shouldSetSelectedBookData) {
            setSelectedBookData({ ...bandData });
        }
        return Promise.resolve(bandData);
    }

    async function loadUnitsForBand(bandId: string): Promise<BandUnit[]> {
        const unitsDataResponse = await getRequest("p6u/get-units-for-band/", { bandId });
        if (!unitsDataResponse || !unitsDataResponse.data) {
            return Promise.reject(new Error("No data was loaded"));
        }

        const sortedUnits = sortAndFilterUnitsForBandResponse(unitsDataResponse.data);
        setSelectedBandUnits(sortedUnits);
        return Promise.resolve(sortedUnits);
    }

    async function initAppWithBookAndUnit(
        articleUuid?: string,
        unitFrom: string = "",
        unitTo: string = ""
    ): Promise<StartPageRedirectionTarget> {
        const stored = takeStoredBookDeepLinkParams();
        // stored units only apply to the stored article
        if (!articleUuid) {
            ({ articleUuid, unitFromUuid: unitFrom, unitToUuid: unitTo } = stored);
        }
        articleUuid = articleUuid.trim();
        unitFrom = unitFrom.trim();
        unitTo = unitTo.trim();

        let redirectTo: StartPageRedirectionTarget = "BOOK";
        if (articleUuid) {
            setIsDataBeingLoaded(true);
            const loadInitArticleResponse = await loadInitArticleIdData(articleUuid, true);
            const bandId = loadInitArticleResponse.band?.ID;

            if (!bandId) {
                setIsDataBeingLoaded(false);
                return Promise.reject();
            }

            const unitsForBandResponse = await loadUnitsForBand(bandId);

            const unitsToLoad: Array<string> = [];
            if (unitFrom && unitTo) {
                const unitFromInfo = unitsForBandResponse.find((u) => u.uuid === unitFrom);
                const unitToInfo = unitsForBandResponse.find((u) => u.uuid === unitTo);

                if (unitFromInfo?.order_id && unitToInfo?.order_id) {
                    let minOrder = unitFromInfo.order_id;
                    let maxOrder = unitToInfo.order_id;

                    if (minOrder > maxOrder) {
                        [minOrder, maxOrder] = [maxOrder, minOrder];
                    }

                    unitsForBandResponse.forEach((unit) => {
                        if (unit.order_id && unit.order_id >= minOrder && unit.order_id <= maxOrder) {
                            unit?.id && unitsToLoad.push(unit.id);
                        }
                    });
                } else {
                    unitFromInfo?.id && unitsToLoad.push(unitFromInfo.id);
                    unitToInfo?.id && unitsToLoad.push(unitToInfo.id);
                }
            } else if (unitFrom) {
                const unitFromId = unitsForBandResponse.find((unit) => unit.uuid === unitFrom)?.id;
                if (unitFromId) {
                    unitsToLoad.push(unitFromId);
                }
            }

            if (unitsToLoad.length > 0) {
                await loadCardsForUnits(unitsToLoad);
                if (creatingMode === CreationModes.RECURRING_TASK) {
                    unitsToLoad.forEach((u) => localStorage.setItem("open_" + u, "true"));
                } else {
                    setUnitsToPreloadIds(unitsToLoad);
                }
                redirectTo = "CARDS";
            }
            setIsDataBeingLoaded(false);
        } else {
            return Promise.reject();
        }
        return Promise.resolve(redirectTo);
    }

    function resetAppStatus() {
        setTestContent([]);
        setTestName("");
        setSelectedTitleTag(null);
        setCurrentTestId("");
        addTestInFlightPromise = null; // don't let a prior test's ADD id leak into a new flow
        setIsTestBeingEdited(false);
        setCurrentExerciseModel({});
        setShowScores(false);
        setSettings(getStandardOrDefaultConfiguration().settings);
        setSelectedBookData({});
        setSelectedUserSubject({});
        setShouldSubjectSelectionBeDisabled(false);
        setPreviouslyUsedCardsIds([]);
        setPreviouslyUsedCardsClassName("");
        setUserOwnSubjectUnits([]);
        setVocabularyDrawerContent({});
        setCreatingMode("");
        setHasConfirmedLargeVocabTraining(false);
        setPendingVocabCardsToAdd([]);
    }

    function removeContentFromVocabularyDrawer(contentType: "ALL" | "OWN" | "BOOK") {
        let idsToDelete: Array<string>;
        if (contentType === "ALL") {
            idsToDelete = Object.keys(vocabularyDrawerContent);
        } else {
            const drawerContentEntries = Object.entries(vocabularyDrawerContent);
            if (contentType === "OWN") {
                idsToDelete = drawerContentEntries
                    .filter(
                        ([key, value]) =>
                            value.wordContent && "isUserCard" in value.wordContent && value.wordContent.isUserCard
                    )
                    .map(([key, value]) => key);
            } else {
                idsToDelete = drawerContentEntries
                    .filter(([key, value]) => value.wordContent && "article_id" in value.wordContent)
                    .map(([key, value]) => key);
            }
        }
        removeCardsFromSelectedWords(idsToDelete);
    }

    // Switching content source starts the title fresh — but only once the tag has resolved into a
    // real title (mirrored into testName). An unresolved tag is kept as a placeholder, so e.g.
    // picking "Based on the book" right before heading to the book selection survives the switch.
    // A custom title (no tag) is left untouched.
    function _clearSelectedTitleTag() {
        // "Today's date" doesn't derive from the content source (it's always resolved), so it never
        // goes stale on a switch and must survive it.
        if (selectedTitleTag === VocabTrainingTitleTags.TODAYS_DATE) {
            return;
        }
        if (selectedTitleTag !== null && testName) {
            setSelectedTitleTag(null);
            setTestName("");
        }
    }

    // Cleans up everything the schoolbook path produced (book, its units, the book vocab in the drawer
    // and the selected title tag) when the teacher switches to own content. Without this, a previously
    // picked book keeps resolving the title and lingers in the drawer on the own-content screen.
    function clearSchoolbookSelection() {
        setSelectedBookData({});
        setSelectedBandUnits([]);
        setPreviouslyUsedCardsIds([]);
        setPreviouslyUsedCardsClassName("");
        removeContentFromVocabularyDrawer("BOOK");
        _clearSelectedTitleTag();
        // The user's own content never has a book, so even an unresolved "Based on the book" placeholder can't
        // survive the switch — it could never resolve there.
        if (selectedTitleTag === VocabTrainingTitleTags.BASED_ON_BOOK) {
            setSelectedTitleTag(null);
            setTestName("");
        }
    }

    // Mirror of clearSchoolbookSelection for the other direction: drops the own-content selection
    // (subject, its units, the own vocab in the drawer and the selected title tag) when the teacher
    // switches to the schoolbook path, so it doesn't linger on the book/vocab screens.
    function clearOwnContentSelection() {
        setSelectedUserSubject({});
        setUserOwnSubjectUnits([]);
        setPreviouslyUsedCardsIds([]);
        setPreviouslyUsedCardsClassName("");
        removeContentFromVocabularyDrawer("OWN");
        setShouldSubjectSelectionBeDisabled(false);
        _clearSelectedTitleTag();
    }

    /** Resolves to null when the request failed, as opposed to an empty list for a teacher without own content. */
    function reloadUserSubjects(): Promise<Array<UserSubject> | null> {
        if (!reloadUserSubjectsInFlight) {
            reloadUserSubjectsInFlight = fetchUserSubjects().finally(() => {
                reloadUserSubjectsInFlight = null;
            });
        }
        return reloadUserSubjectsInFlight;
    }

    async function fetchUserSubjects(): Promise<Array<UserSubject> | null> {
        setIsDataBeingLoaded(true);
        try {
            const userSubjectsResponse = await makePxpRequest("GET", "userSubjectContents");
            const processedResponse: Array<UserSubject> = [];
            const availableSubjects: AvailableUserOwnSubjectsInfo = {};
            if (
                userSubjectsResponse.data.replyContent.objects &&
                !!userSubjectsResponse.data.replyContent.objects.length
            ) {
                userSubjectsResponse.data.replyContent.objects.forEach((subject: any) => {
                    const subj = { ...subject[0], ...subject[1] };
                    processedResponse.push(subj);
                    availableSubjects[subj.id] = subj;
                });
            }
            setUserOwnSubjects(processedResponse);
            setUserUsedOwnSubjectsInfo(availableSubjects);
            setHaveOwnSubjectsLoaded(true);
            return processedResponse;
        } catch (e) {
            console.error(e);
            return null;
        } finally {
            setIsDataBeingLoaded(false);
        }
    }

    // One units request per subject at a time, and only the subject requested last may write: the
    // subject-selection effects ask twice back to back, and a teacher can switch subjects mid-request.
    const userUnitsLoadRef = useRef<{ subjectUUID: string; promise: Promise<void> } | null>(null);

    function loadUserUnitsForSubject(subjectUUID: string): Promise<void> {
        const inFlight = userUnitsLoadRef.current;
        if (inFlight?.subjectUUID === subjectUUID) return inFlight.promise;

        const selectedSubject = userOwnSubjects.find((s) => s.id === subjectUUID);
        if (!selectedSubject) return Promise.resolve();

        const promise: Promise<void> = fetchUserUnitsForSubject(selectedSubject, subjectUUID).finally(() => {
            if (userUnitsLoadRef.current?.promise === promise) userUnitsLoadRef.current = null;
        });
        userUnitsLoadRef.current = { subjectUUID, promise };
        return promise;
    }

    async function fetchUserUnitsForSubject(selectedSubject: UserSubject, subjectUUID: string): Promise<void> {
        setIsDataBeingLoaded(true);
        setSelectedUserSubject(selectedSubject);

        try {
            const userUnitResponse = await makePxpRequest("GET", "userUnitsContents/" + subjectUUID);
            // Stale: a newer subject was requested and owns the loading state.
            if (userUnitsLoadRef.current?.subjectUUID !== subjectUUID) return;

            const processedResponse: Array<UserUnit> = [];
            const unitIds: Array<string> = [];
            if (userUnitResponse.data.httpCode === 200 && userUnitResponse.data.replyContent.objects.length) {
                userUnitResponse.data.replyContent.objects.forEach((unit: any) => {
                    const subjectIdToOwnerData = unit[0];
                    const subjectData = unit[1];
                    processedResponse.push({ ...subjectIdToOwnerData, ...subjectData, subjectId: subjectUUID });
                    unitIds.push(subjectIdToOwnerData.id);
                });
            } else if (userUnitResponse.data.httpCode === 401) {
                if (userUnitResponse.data.exceptionClass === PXPErrorClasses.Unauthorized) {
                    setIsDataBeingLoaded(false);
                    history.push({
                        pathname: "/auth",
                        search: creatingMode === CreationModes.RECURRING_TASK ? "?redirectTo=/assistent" : "",
                    });
                } else {
                    throw new Error(userUnitResponse.data.exceptionMessage);
                }
            }
            setUserOwnSubjectUnits(processedResponse);
            // Reloading a subject keeps the cards its units already loaded, or an expanded unit would
            // empty under the teacher.
            setUserUnitCards((prevCards) => {
                const nextCards: UserUnitCards = {};
                unitIds.forEach((id) => {
                    nextCards[id] = prevCards[id] ?? [];
                });
                return nextCards;
            });
            setIsDataBeingLoaded(false);
            return;
        } catch (e) {
            console.log(e);
        }
        setIsDataBeingLoaded(false);
    }

    async function loadUserUnitCards(unitUUID: string) {
        setIsDataBeingLoaded(true);

        const subjectUUID = selectedUserSubject.id;
        if (!subjectUUID) {
            setIsDataBeingLoaded(false);
            return;
        }

        try {
            const userUnitResponse = await makePxpRequest("GET", `userCardsContents/${subjectUUID}/${unitUUID}`);
            const processedResponse = processUserUnitCardsResponse(
                userUnitResponse.data.replyContent.objects,
                subjectUUID,
                unitUUID
            );
            setUserUnitCards((prevCards) => ({ ...prevCards, [unitUUID]: processedResponse }));
            setIsDataBeingLoaded(false);
            return Promise.resolve();
        } catch (e) {
            console.log(e);
        }
        setIsDataBeingLoaded(false);
    }

    // Display-only, so a failure degrades to "no sharing info" rather than propagating. Destructive callers
    // of getTeacherSharedTests must not reuse this — they need the rejection to stop the operation.
    async function loadUserTestsSharingData() {
        setIsTestSharingInfoBeingLoaded(true);
        try {
            const studentTestsContent: StudentTestContentWrapper = await getTeacherSharedTests();
            setStudentTestContentWrapper(studentTestsContent);
        } catch (e) {
            console.error("Failed to load test sharing data", e);
        } finally {
            setIsTestSharingInfoBeingLoaded(false);
        }
    }

    // Returning the length of the user's tests.
    async function loadUserTests(forceReload = false) {
        if (userId && (!dataLoaded || forceReload)) {
            setIsDataBeingLoaded(true);
            try {
                const userTests: Array<TeacherTestDetails> = await getUserTests();
                setUserTests(userTests);

                // Loading this after it's set to avoid inconsistencies.
                setIsTestSharingInfoBeingLoaded(true);
                loadUserTestsSharingData();
                reloadUserSubjects();

                setUserTestsLoadError(false);
                setDataLoaded(true);
                // Returning the amount of the user's tests.
                return Promise.resolve(userTests.length);
            } catch (e: any) {
                if (e?.response?.status === NetworkErrorCodes.FORBIDDEN) {
                    // The redirect is the handling — most callers fire this bare, so rejecting on top of it
                    // would only produce unhandled rejections nobody acts on.
                    history.push("/unauthorized");
                    return;
                }
                // Surface the error inline (instead of a popup) and keep any previously
                // loaded tests so onboarding doesn't regress on a transient failure.
                setUserTestsLoadError(true);
                setDataLoaded(true);
                console.log(e);
            } finally {
                setIsDataBeingLoaded(false);
            }
        }
    }

    function openWebAppInAddCardsMode() {
        if (userInfo.addCardsAutoLoginUrl && userInfo.addCardsAutoLoginUrl.length > 1) {
            window.open(userInfo.addCardsAutoLoginUrl, "_blank");
        }
    }

    async function decideGoToOwnSubjectsAction(forceNewContentCheck?: boolean): Promise<OwnSubjectsAction> {
        // The initial fetch is fired without being awaited while the tests load, so until it has
        // resolved the state alone would send a quick click to the no-content popup although the
        // teacher has subjects.
        if (!forceNewContentCheck && haveOwnSubjectsLoaded) {
            return userOwnSubjects.length ? "CONTINUE" : "POPUP";
        }
        const subjects = await reloadUserSubjects();
        if (subjects === null) {
            openWarningModal(t_errorLoadingSubjectInfo);
            return "ERROR";
        }
        return subjects.length ? "CONTINUE" : "POPUP";
    }

    // Resolves whether the new groupId reached the backend. A test missing from userTests cannot be written
    // (the PUT needs its full body), which is a failure to assign — not a no-op to be read as success.
    async function updateTestSchoolClass(
        testId: string,
        schoolClassId: string | null,
        options?: SilentErrorOptions
    ): Promise<boolean> {
        const currentTest = userTests.find((test) => test.id === testId);
        if (!currentTest) return false;

        try {
            await putRequest(`aea/tests/?testId=${testId}`, {
                ...currentTest,
                ...toEpochMsTimestamps(currentTest),
                groupId: schoolClassId,
            });
            setUserTests(
                userTests.map((test) =>
                    test.id === testId
                        ? { ...test, ...toEpochMsTimestamps(test), groupId: schoolClassId ?? undefined }
                        : test
                )
            );
            return true;
        } catch (e) {
            if (!options?.silentOnError) {
                openWarningModal(t_errorUpdatingTestClass);
            }
            throw e;
        }
    }

    /**
     * Reassigns a shared (or legacy-shared) test/practice to a different class by creating a copy
     * with the new groupId, re-sharing it if sharedContent is provided (preserving the original
     * share metadata), and then deleting the original. Returns the new test id, or null on failure.
     * Pass sharedContent=null to intentionally drop sharing (e.g. shared test reassigned to a public class).
     */
    async function reassignSharedTestToClass(params: {
        testId: string;
        newSchoolClassId: string | null;
        sharedContent: StudentTestContent | null;
    }): Promise<string | null> {
        const { testId, newSchoolClassId, sharedContent } = params;
        const original = userTests.find((test) => test.id === testId);
        if (!original) {
            openWarningModal(t_noTestFound);
            return null;
        }

        // A preserved legacy share resolves to an explicit TEST, so the copy is no longer legacy.
        const shareMode = sharedContent ? resolveShareModeWithLegacy(sharedContent) : null;

        const newTestId = await reassignSharedTest({
            originalTestId: testId,
            createCopy: async () => {
                const createResp = await postRequest(
                    "aea/tests/",
                    _buildTestCopyPayload(original, newSchoolClassId, shareMode)
                );
                return createResp.data.id as string;
            },
            shareCopy:
                sharedContent && shareMode
                    ? (createdTestId) =>
                          postRequest(
                              `aea/student-tests/?testId=${createdTestId}`,
                              _buildSharedCopyPayload(sharedContent, shareMode, newSchoolClassId)
                          )
                    : null,
            deleteTest: (idToDelete) => deleteRequest(`aea/tests/?testId=${idToDelete}`),
            captureError: (error, extra) => {
                console.error("reassignSharedTestToClass failed", error, extra);
                Sentry.captureException(error, { extra });
            },
        });
        if (!newTestId) {
            openWarningModal(t_errorUpdatingTestClass);
            return null;
        }

        // Outside the transaction: a failing refresh must never trigger the rollback.
        // loadUserTests(true) also triggers loadUserTestsSharingData().
        await loadUserTests(true);

        logEvent("Shared Test Reassigned", {
            testId,
            newTestId,
            newClassId: newSchoolClassId ?? "removed",
            preservedShare: !!sharedContent,
        });
        return newTestId;
    }

    return {
        ...context,
        duplicateTest,
        deleteTest,
        shareTest,
        removeCardsFromSelectedWords,
        loadTestForEdition,
        loadVocabularyForBand,
        removeCardFromSelectedWords,
        loadCardsForUnit,
        addCardToSelectedWords,
        addCardsToSelectedWords,
        setDrawerContentFromCards,
        confirmLargeVocabTraining,
        cancelLargeVocabTraining,
        isLargeVocabConfirmVisible: pendingVocabCardsToAdd.length > 0,
        openWarningModal,
        prepareReducedTestDetails,
        prepareFullTestDetails,
        saveTest,
        createPreviewData,
        updateCurrentExerciseProperties,
        checkForAnonymousTests,
        syncSharedTestCopy,
        updateSharedTestProperties,
        loadUserTests,
        loadUserUnitsForSubject,
        loadUserUnitCards,
        loadUserOwnSubjectUUIDsInfoIntoState,
        resetAppStatus,
        reloadUserSubjects,
        loadInitArticleIdData,
        removeContentFromVocabularyDrawer,
        clearSchoolbookSelection,
        clearOwnContentSelection,
        initAppWithBookAndUnit,
        openWebAppInAddCardsMode,
        decideGoToOwnSubjectsAction,
        updateTestSchoolClass,
        reassignSharedTestToClass,
    };
}

function TestCreationContextProvider(props: any) {
    const [userTests, setUserTests] = useState<Array<TeacherTestDetails>>([]);
    const [userTestsLoadError, setUserTestsLoadError] = useState(false);
    const [studentTestContentWrapper, setStudentTestContentWrapper] = useState<StudentTestContentWrapper>({});
    const studentTestContentWrapperRef = useRef(studentTestContentWrapper);
    studentTestContentWrapperRef.current = studentTestContentWrapper;
    const [dataLoaded, setDataLoaded] = useState(false);
    const [isTestSynced, setIsTestSynced] = useState<boolean | undefined>();
    const [isTitleSynced, setIsTitleSynced] = useState<boolean>(true);
    const [selectedBookData, setSelectedBookData] = useState<SelectedBookData>({});

    const [availableVerbtrainingTenses, setAvailableVerbtrainingTenses] =
        useState<Array<VerbtrainingTensesResponseItem>>(verbtrainingTenses);
    const [isVocabularyDrawerOpen, setIsVocabularyDrawerOpen] = useState(false);

    const [selectedBandUnits, setSelectedBandUnits] = useState<Array<BandUnit>>([]);

    const [availableCards, setAvailableCards] = useState<BandCards>({});
    const [vocabularyDrawerContent, setVocabularyDrawerContent] = useState<SidebarSelectedWordsList>({});

    const [maxAmountOfCardsSelectable, setMaxAmountOfCardsSelectable] = useState<number>(50);
    const [isWarningModalVisible, setIsWarningModalVisible] = useState(false);
    const [warningModalContent, setWarningModalContent] = useState<React.ReactNode>();

    // Vocab trainings have no hard limit, but the teacher confirms once when crossing maxAmountOfCardsSelectable.
    const [hasConfirmedLargeVocabTraining, setHasConfirmedLargeVocabTraining] = useState(false);
    const [pendingVocabCardsToAdd, setPendingVocabCardsToAdd] = useState<Array<UnitCard>>([]);

    const [testContent, setTestContent] = useState<Array<TestQuestionModel>>([]);
    const [testName, setTestName] = useState("");
    const [isAnonymousTestClaimed, setIsAnonymousTestClaimed] = useState(false);
    const [selectedTitleTag, setSelectedTitleTag] = useState<VocabTrainingTitleTags | null>(null);

    const [currentTestId, setCurrentTestId] = useState("");

    const [currentExerciseModel, setCurrentExerciseModel] = useState<ExerciseCreationModel>({});

    const [showScores, setShowScores] = useState(false);

    const [settings, setSettings] = useState<Settings>(getStandardOrDefaultConfiguration().settings);

    const [isTestBeingEdited, setIsTestBeingEdited] = useState(false);
    const [isTestSharingInfoBeingLoaded, setIsTestSharingInfoBeingLoaded] = useState(false);
    const [isFirstCreateHomeLoad, setIsFirstCreateHomeLoad] = useState(true);

    const [userOwnSubjects, setUserOwnSubjects] = useState<Array<UserSubject>>([]);
    const [haveOwnSubjectsLoaded, setHaveOwnSubjectsLoaded] = useState(false);
    const [userOwnSubjectUnits, setUserOwnSubjectUnits] = useState<Array<UserUnit>>([]);
    const [selectedUserSubject, setSelectedUserSubject] = useState<UserSubject>({});
    const [userUnitCards, setUserUnitCards] = useState<UserUnitCards>({});
    const [shouldSubjectSelectionBeDisabled, setShouldSubjectSelectionBeDisabled] = useState(false);

    // Exclusive vars for the vokabeltrainer exercises (recurring tasks)
    const [userUsedOwnSubjectsInfo, setUserUsedOwnSubjectsInfo] = useState<AvailableUserOwnSubjectsInfo>({});
    const [previouslyUsedCardsIds, setPreviouslyUsedCardsIds] = useState<Array<string>>([]);
    const [previouslyUsedCardsClassName, setPreviouslyUsedCardsClassName] = useState<string>("");
    const [unitsToPreloadIds, setUnitsToPreloadIds] = useState<Array<any>>([]);

    const value = {
        userTests,
        setUserTests,
        userTestsLoadError,
        setUserTestsLoadError,
        isTestSynced,
        setIsTestSynced,
        isTitleSynced,
        setIsTitleSynced,
        studentTestContentWrapper,
        studentTestContentWrapperRef,
        setStudentTestContentWrapper,
        dataLoaded,
        setDataLoaded,
        availableVerbtrainingTenses,
        setAvailableVerbtrainingTenses,
        selectedBookData,
        setSelectedBookData,
        isVocabularyDrawerOpen,
        setIsVocabularyDrawerOpen,
        vocabularyDrawerContent,
        setVocabularyDrawerContent,
        selectedBandUnits,
        setSelectedBandUnits,
        availableCards,
        setAvailableCards,
        maxAmountOfCardsSelectable,
        setMaxAmountOfCardsSelectable,
        isWarningModalVisible,
        setIsWarningModalVisible,
        warningModalContent,
        setWarningModalContent,
        hasConfirmedLargeVocabTraining,
        setHasConfirmedLargeVocabTraining,
        pendingVocabCardsToAdd,
        setPendingVocabCardsToAdd,
        testContent,
        setTestContent,
        testName,
        setTestName,
        isAnonymousTestClaimed,
        setIsAnonymousTestClaimed,
        selectedTitleTag,
        setSelectedTitleTag,
        currentTestId,
        setCurrentTestId,
        currentExerciseModel,
        setCurrentExerciseModel,
        showScores,
        setShowScores,
        settings,
        setSettings,
        isTestBeingEdited,
        setIsTestBeingEdited,
        isTestSharingInfoBeingLoaded,
        setIsTestSharingInfoBeingLoaded,
        previouslyUsedCardsIds,
        setPreviouslyUsedCardsIds,
        previouslyUsedCardsClassName,
        setPreviouslyUsedCardsClassName,
        isFirstCreateHomeLoad,
        setIsFirstCreateHomeLoad,
        userOwnSubjects,
        setUserOwnSubjects,
        haveOwnSubjectsLoaded,
        setHaveOwnSubjectsLoaded,
        userOwnSubjectUnits,
        setUserOwnSubjectUnits,
        selectedUserSubject,
        setSelectedUserSubject,
        userUnitCards,
        setUserUnitCards,
        userUsedOwnSubjectsInfo,
        setUserUsedOwnSubjectsInfo,
        shouldSubjectSelectionBeDisabled,
        setShouldSubjectSelectionBeDisabled,
        unitsToPreloadIds,
        setUnitsToPreloadIds,
    };

    return (
        <TestCreationContext.Provider
            value={value}
            {...props}
        />
    );
}

export { TestCreationContextProvider, useTestCreationContext };
